import type { SiteSnapshot } from "@/data/types";
import { signedKW } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { scada } from "../scadaColors";

/**
 * Grid coupling — the front-of-meter node.
 *
 * Compositionally: a transmission pylon to the left, a transformer kiosk
 * to the right, and a small data card below. Drop shadow + clear silhouette
 * so it reads at a glance even at 1/3 the on-screen size.
 */
export function GridNode({
  cx, cy, snap, importing, exporting, detail = true,
}: {
  cx: number; cy: number; snap: SiteSnapshot;
  importing: boolean; exporting: boolean;
  /** Summary mode (false) hides the freq/V instrument row. */
  detail?: boolean;
}) {
  const t = useT();
  const PURPLE = scada.gridImport();   // import (matches palette in styles.css)
  const EXPORT = scada.gridExport();
  const valueColor = importing ? PURPLE : exporting ? EXPORT : "var(--text-muted)";
  const gid = `grid-grad-${Math.round(cx)}-${Math.round(cy)}`;
  const xgid = `grid-xfrm-${Math.round(cx)}-${Math.round(cy)}`;

  return (
    <g style={{ cursor: "pointer" }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="var(--scada-surface-hi)" />
          <stop offset="100%" stopColor="var(--scada-surface-mid)" />
        </linearGradient>
        <linearGradient id={xgid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="var(--scada-surface-mid)" />
          <stop offset="100%" stopColor="var(--scada-surface-lo)" />
        </linearGradient>
      </defs>

      {/* Drop shadow */}
      <ellipse cx={cx} cy={cy + 60} rx={70} ry={5} fill="var(--scada-shadow)" />

      {/* Background plate (rounded) */}
      <rect x={cx - 78} y={cy - 60} width={156} height={120} rx={14}
        fill={`url(#${gid})`}
        stroke={PURPLE} strokeOpacity={0.45} strokeWidth={1.4} />
      {/* Plate header bar */}
      <rect x={cx - 78} y={cy - 60} width={156} height={16} rx={14}
        fill={PURPLE} fillOpacity={0.08} />
      {/* No voltage-level suffix — the topology doesn't carry the grid
          coupling's voltage class, so the plate must not claim one
          (OVW-10: wrong on MV-metered sites). */}
      <text x={cx - 70} y={cy - 49} fontSize={8} fontWeight={700} fill={PURPLE} letterSpacing={1.2}>
        {t("monitor.scada.node.grid")}
      </text>
      <circle cx={cx + 66} cy={cy - 52} r={2.2} fill={PURPLE} />

      {/* Pylon (left) — three crossarms with insulators */}
      <g transform={`translate(${cx - 56} ${cy - 30})`}>
        {/* Tower silhouette: trapezoid legs */}
        <path d="M0,72 L14,4 M28,72 L14,4" stroke={PURPLE} strokeWidth={1.4} fill="none" />
        {/* Three crossarms */}
        <line x1={0} y1={20} x2={28} y2={20} stroke={PURPLE} strokeWidth={1.6} />
        <line x1={2} y1={36} x2={26} y2={36} stroke={PURPLE} strokeWidth={1.4} />
        <line x1={4} y1={52} x2={24} y2={52} stroke={PURPLE} strokeWidth={1.2} />
        {/* Insulator pendants on top crossarm */}
        {[2, 14, 26].map((ix, i) => (
          <g key={i}>
            <line x1={ix} y1={20} x2={ix} y2={26} stroke={PURPLE} strokeWidth={1} />
            <circle cx={ix} cy={27} r={1.4} fill={PURPLE} />
          </g>
        ))}
        {/* Lattice cross-bracing */}
        <path d="M2,26 L26,40 M26,26 L2,40 M4,42 L24,56 M24,42 L4,56 M6,58 L22,72 M22,58 L6,72"
          stroke={PURPLE} strokeOpacity={0.45} strokeWidth={0.6} fill="none" />
      </g>

      {/* Catenary span line to transformer */}
      <path
        d={`M${cx - 28},${cy - 26} Q${cx - 12},${cy - 18} ${cx + 6},${cy - 14}`}
        stroke={PURPLE} strokeWidth={1.1} fill="none"
      />

      {/* Transformer (right): MV/LV unit with cooling fins + bushings */}
      <g>
        {/* Body — gradient fill for subtle 3D look */}
        <rect x={cx + 10} y={cy - 18} width={48} height={48} rx={3}
          fill={`url(#${xgid})`} stroke={PURPLE} strokeWidth={1.4} />
        {/* Cooling fins */}
        {[0, 1, 2, 3, 4].map((i) => (
          <line key={`L-${i}`} x1={cx + 7} y1={cy - 14 + i * 10} x2={cx + 10} y2={cy - 14 + i * 10}
            stroke={PURPLE} strokeWidth={1.1} />
        ))}
        {[0, 1, 2, 3, 4].map((i) => (
          <line key={`R-${i}`} x1={cx + 58} y1={cy - 14 + i * 10} x2={cx + 61} y2={cy - 14 + i * 10}
            stroke={PURPLE} strokeWidth={1.1} />
        ))}
        {/* Oil expansion drum */}
        <ellipse cx={cx + 34} cy={cy - 22} rx={11} ry={4}
          fill="var(--surface-3)" stroke={PURPLE} strokeWidth={1} />
        {/* HV bushings */}
        {[18, 34, 50].map((bx, i) => (
          <g key={i}>
            <line x1={cx + bx} y1={cy - 26} x2={cx + bx} y2={cy - 32}
              stroke={PURPLE} strokeWidth={1.6} strokeLinecap="round" />
            <circle cx={cx + bx} cy={cy - 32} r={1.6} fill={PURPLE} />
          </g>
        ))}
        {/* No "MV / LV" stamp — a hardcoded voltage-level claim is wrong
            on medium-voltage sites (OVW-10); the transformer glyph alone
            carries the meaning. */}
        {/* Outlet line */}
        <line x1={cx + 34} y1={cy + 30} x2={cx + 34} y2={cy + 36}
          stroke={PURPLE} strokeWidth={1.6} />
      </g>

      {/* Data row — secondary instrument strip, collapsed in Summary mode. */}
      {detail && (
        <g transform={`translate(${cx - 70} ${cy + 36})`}>
          <rect width={140} height={20} rx={4} fill="var(--surface-3)" stroke="var(--edge)" strokeWidth={0.7} />
          <Datum x={6} label="freq" value={`${snap.grid.frequency_Hz.toFixed(2)} Hz`} title={t("monitor.scada.node.gridFrequency")} />
          <line x1={66} y1={4} x2={66} y2={16} stroke="var(--edge)" />
          <Datum x={72} label="V" value={`${snap.grid.voltage_V.toFixed(0)} V`} />
        </g>
      )}

      {/* Footer label */}
      <text x={cx} y={cy + 76} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--text)">
        Grid coupling
      </text>
      <text x={cx} y={cy + 90} textAnchor="middle" fontSize={14} fontWeight={800}
        className="num" fill={valueColor}>
        {signedKW(snap.grid.active_W)} kW
      </text>
      <text x={cx} y={cy + 102} textAnchor="middle" fontSize={9} fill="var(--text-muted)">
        {/* Native SVG tooltip expanding the abbreviation (OVW-12). */}
        <title>{t("monitor.scada.node.powerFactor")}</title>
        {importing ? t("status.importing") : exporting ? t("status.exporting") : t("status.idle")} · pf {snap.grid.pf.toFixed(2)}
      </text>
    </g>
  );
}

function Datum({ x, label, value, title }: { x: number; label: string; value: string; title?: string }) {
  return (
    <g>
      {title && <title>{title}</title>}
      <text x={x} y={12} fontSize={7.5} fill="var(--text-faint)" letterSpacing={0.3}>{label.toUpperCase()}</text>
      <text x={x + 22} y={13} fontSize={9.5} fill="var(--text)" fontWeight={700} className="num">{value}</text>
    </g>
  );
}
